import React from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableContainer,
  Skeleton,
  Typography,
  Select,
  MenuItem,
  IconButton,
  useTheme,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, ChevronRight } from './Icons';
import EmptyState from './EmptyState';

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

/**
 * Opinionated table wrapper used across back-office list pages.
 *
 * columns: [{ id, header, align, width, render: (row) => node, hideOnSm: bool }]
 * rows:    array of records
 * loading: boolean — renders 5 skeleton rows
 * empty:   { icon, title, description, action } passed to EmptyState
 * pagination: { page, pageSize, total, onPageChange, onPageSizeChange }
 *             (omit to disable footer)
 * onRowClick: (row) => void
 */
const DataTable = ({
  columns = [],
  rows = [],
  loading = false,
  empty,
  pagination,
  onRowClick,
  getRowKey,
  ariaLabel,
}) => {
  const theme = useTheme();
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language === 'ar';

  const ChevronStart = isRtl ? ChevronRight : ChevronLeft;
  const ChevronEnd = isRtl ? ChevronLeft : ChevronRight;

  const showSkeleton = loading;
  const showEmpty = !loading && rows.length === 0;

  // Pagination math
  const page = pagination?.page ?? 0;
  const pageSize = pagination?.pageSize ?? 10;
  const total = pagination?.total ?? rows.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const firstRow = total === 0 ? 0 : page * pageSize + 1;
  const lastRow = Math.min((page + 1) * pageSize, total);

  return (
    <Box>
      <TableContainer
        sx={{
          backgroundColor: 'background.paper',
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'divider',
          // Match the design-system card elevation (same value as the MuiCard
          // theme override) so the table sits on the page like the KPI cards.
          boxShadow: '0 1px 3px rgba(15, 23, 42, 0.04), 0 1px 2px rgba(15, 23, 42, 0.03)',
        }}
      >
        <Table aria-label={ariaLabel} sx={{ minWidth: 600 }}>
          <TableHead>
            <TableRow sx={{ '& th': { backgroundColor: 'action.hover', borderBottom: '1px solid', borderColor: 'divider' } }}>
              {columns.map((col, colIdx) => (
                <TableCell
                  key={col.id}
                  align={col.align || 'inherit'}
                  sx={{
                    fontWeight: 600,
                    fontSize: 13,
                    color: 'text.secondary',
                    textTransform: 'uppercase',
                    letterSpacing: '0.04em',
                    width: col.width,
                    // Round the tinted header band's leading/trailing top corners to
                    // match the container radius. Logical props flip under RTL.
                    borderStartStartRadius: colIdx === 0 ? 8 : 0,
                    borderStartEndRadius: colIdx === columns.length - 1 ? 8 : 0,
                    display: col.hideOnSm
                      ? { xs: 'none', md: 'table-cell' }
                      : 'table-cell',
                  }}
                >
                  {col.header}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {showSkeleton &&
              Array.from({ length: 5 }).map((_, i) => (
                <TableRow key={`skel-${i}`}>
                  {columns.map((col) => (
                    <TableCell
                      key={col.id}
                      sx={{ display: col.hideOnSm ? { xs: 'none', md: 'table-cell' } : 'table-cell' }}
                    >
                      <Skeleton variant="text" sx={{ fontSize: 14 }} />
                    </TableCell>
                  ))}
                </TableRow>
              ))}

            {!showSkeleton && !showEmpty &&
              rows.map((row, idx) => (
                <TableRow
                  key={getRowKey ? getRowKey(row, idx) : row.id ?? idx}
                  onClick={onRowClick ? () => onRowClick(row) : undefined}
                  sx={{
                    cursor: onRowClick ? 'pointer' : 'default',
                    transition: 'background-color 120ms ease',
                    // Subtle hover on every row for scannability, not just clickable ones.
                    '&:hover': { backgroundColor: 'action.hover' },
                    '&:last-child td': { borderBottom: 0 },
                  }}
                >
                  {columns.map((col) => (
                    <TableCell
                      key={col.id}
                      align={col.align || 'inherit'}
                      sx={{
                        py: 2,
                        color: 'text.primary',
                        fontSize: 14,
                        display: col.hideOnSm ? { xs: 'none', md: 'table-cell' } : 'table-cell',
                      }}
                    >
                      {col.render ? col.render(row, idx) : row[col.id]}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
          </TableBody>
        </Table>

        {showEmpty && (
          <Box sx={{ py: 4 }}>
            <EmptyState
              icon={empty?.icon}
              title={empty?.title || t('common.noData', 'Nothing here yet')}
              description={empty?.description}
              action={empty?.action}
            />
          </Box>
        )}
      </TableContainer>

      {pagination && total > 0 && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 2,
            mt: 2,
            px: 1,
          }}
        >
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            {t('table.showingRange', {
              defaultValue: 'Showing {{from}} to {{to}} of {{total}}',
              from: firstRow,
              to: lastRow,
              total,
            })}
          </Typography>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <IconButton
                size="small"
                onClick={() => pagination.onPageChange?.(Math.max(0, page - 1))}
                disabled={page <= 0}
                aria-label={t('table.previousPage', 'Previous page')}
              >
                <ChevronStart size={18} />
              </IconButton>
              <Typography variant="body2" sx={{ mx: 1, color: 'text.secondary' }}>
                {page + 1} / {totalPages}
              </Typography>
              <IconButton
                size="small"
                onClick={() => pagination.onPageChange?.(Math.min(totalPages - 1, page + 1))}
                disabled={page >= totalPages - 1}
                aria-label={t('table.nextPage', 'Next page')}
              >
                <ChevronEnd size={18} />
              </IconButton>
            </Box>

            {pagination.onPageSizeChange && (
              <Select
                value={pageSize}
                onChange={(e) => pagination.onPageSizeChange(Number(e.target.value))}
                size="small"
                sx={{ minWidth: 110 }}
                aria-label={t('table.pageSize', 'Rows per page')}
              >
                {PAGE_SIZE_OPTIONS.map((size) => (
                  <MenuItem key={size} value={size}>
                    {t('table.pageSizeOption', { defaultValue: '{{count}} / page', count: size })}
                  </MenuItem>
                ))}
              </Select>
            )}
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default DataTable;
